import { ArrowDown } from 'lucide-react';
import { Link } from 'react-router-dom';

import {
  AdmissionProcess,
  AdmissionsFaq,
  CustomTrackBuilder,
  FindAdvisor,
  WhyIbas,
} from '@/components/admissions';
import {
  Magnetic,
  MediaFrame,
  NavyAtmosphere,
  PointerParallax,
  ScrollReveal,
  SplitReveal,
  TextStagger,
  TiltCard,
} from '@/components/motion';
import { EnquiryForm } from '@/components/sections';
import { Button } from '@ibas/ui';

/** Hero media: a "why IBAS" video over a graduation-portrait poster. */
const HERO_VIDEO = '/media/videos/why-ibas.mp4';
const HERO_POSTER = '/media/images/graduation-events/graduate-portrait-cap-gown.webp';

/**
 * AdmissionsPage (/admissions) — the admissions hub. A navy, motion-led hero
 * introduces the section, then the five requested blocks follow in order:
 * Why study with IBAS · Find your admission advisor · Admission process · Build
 * your custom study track · FAQ. The shared enquiry form closes the page.
 *
 * Each section is a self-contained, reduced-motion-safe component reading only
 * verified facts from @/data/admissions (and programme data from
 * @/data/programmes). This page owns the hero, the section order and the closing
 * beats; the sections own their own choreography.
 */
export function AdmissionsPage() {
  return (
    <div className="bg-background">
      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative isolate flex min-h-[88vh] items-center overflow-hidden bg-brand-navy">
        <NavyAtmosphere />

        <div className="relative z-10 mx-auto grid w-full max-w-6xl grid-cols-1 items-center gap-12 px-6 py-20 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
          <div className="max-w-xl">
            <TextStagger
              text="Admissions"
              className="font-label text-xs tracking-[0.24em] text-brand-gold-light uppercase"
            />
            <SplitReveal
              as="h1"
              by="lines"
              className="mt-4 font-display text-4xl leading-[1.05] text-white md:text-6xl"
            >
              Study your way. Begin when you are ready.
            </SplitReveal>
            <ScrollReveal from="up" delay={0.1}>
              <p className="mt-6 max-w-lg text-lg leading-relaxed text-white/70">
                Advancing in today’s business environment requires more than a qualification — it
                requires recognition, credibility and the ability to operate across borders. Here is
                how you join IBAS, choose your route and get the support to start with confidence.
              </p>
            </ScrollReveal>
            <ScrollReveal from="up" delay={0.2}>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Magnetic strength={0.4} radius={140}>
                  <Button asChild size="lg" variant="secondary" data-cursor>
                    <Link to="/apply">Start your application</Link>
                  </Button>
                </Magnetic>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white"
                  asChild
                >
                  <a href="#find-advisor">
                    Connect with an adviser
                    <ArrowDown aria-hidden className="size-4" />
                  </a>
                </Button>
              </div>
            </ScrollReveal>
          </div>

          {/* Hero media plate — pointer parallax over a tilt-reactive frame. */}
          <PointerParallax strength={16} className="mx-auto w-full max-w-md lg:max-w-none">
            <TiltCard className="rounded-2xl" glare>
              <div data-parallax data-depth="0.8">
                <MediaFrame
                  image={HERO_POSTER}
                  video={HERO_VIDEO}
                  alt="Close-up portrait of an IBAS graduate wearing a cap and gown at a graduation ceremony."
                  aspect="aspect-[4/5]"
                  overlay={55}
                  rounded="rounded-2xl"
                  position="50% 30%"
                >
                  <div className="absolute inset-x-6 bottom-6 md:inset-x-8 md:bottom-8">
                    <span className="font-label text-xs tracking-[0.2em] text-brand-gold-light uppercase">
                      Study Your Way. Connect Globally.
                    </span>
                    <p className="mt-1 font-display text-2xl leading-tight text-white md:text-3xl">
                      Your route starts with a conversation.
                    </p>
                  </div>
                </MediaFrame>
              </div>
            </TiltCard>
          </PointerParallax>
        </div>
      </section>

      {/* ── 1 · WHY STUDY WITH IBAS ───────────────────────────────────────── */}
      <div id="why-ibas" className="scroll-mt-24">
        <WhyIbas />
      </div>

      {/* ── 2 · FIND YOUR ADMISSION ADVISOR ───────────────────────────────── */}
      <div id="find-advisor" className="scroll-mt-24">
        <FindAdvisor />
      </div>

      {/* ── 3 · ADMISSION PROCESS ─────────────────────────────────────────── */}
      <div id="process" className="scroll-mt-24">
        <AdmissionProcess />
      </div>

      {/* ── 4 · BUILD YOUR CUSTOM STUDY TRACK ─────────────────────────────── */}
      <div id="custom-track" className="scroll-mt-24">
        <CustomTrackBuilder />
      </div>

      {/* ── 5 · FAQ ───────────────────────────────────────────────────────── */}
      <div id="faqs" className="scroll-mt-24">
        <AdmissionsFaq />
      </div>

      {/* Shared enquiry form (also the /apply target) closes the page. */}
      <div id="enquire" className="scroll-mt-24">
        <EnquiryForm />
      </div>
    </div>
  );
}
